// The observation form, kept in this browser until it is submitted.
//
// Some forty answers and two notes used to live only in React state, so a
// phone discarding a background tab, a failed token refresh bouncing to
// /login, or a submit that never reached the server (the proxy outage of
// 2026-10-01 cost one teacher a whole lesson that way) threw the lesson away.
//
// localStorage, not session: by the time the person comes back for the lesson
// they lost, they have usually closed the tab it was in.
//
// It holds the private note, which the observed teacher must never read, so
// three things bound it: the key names the signed-in account, a draft expires
// after a week, and an explicit sign-out wipes EVERY draft in this browser — a
// shared staff-room computer must not hand one observer's write-up to whoever
// signs in next. A session that merely EXPIRED keeps it: that is the case it
// exists for.

import { safe } from "./storage";

const PREFIX = "obsDraft:v1:";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

// One draft per booked visit; a new observation is keyed on its teacher, so
// starting a second one never overwrites the first.
export const observationDraftKey = ({ userUuid, visitUuid, teacherUuid }) => {
  if (!userUuid) return null;
  if (visitUuid) return `${PREFIX}${userUuid}:visit:${visitUuid}`;
  if (teacherUuid) return `${PREFIX}${userUuid}:new:${teacherUuid}`;
  return null;
};

const draftKeys = () =>
  safe(() => {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PREFIX)) keys.push(key);
    }
    return keys;
  }, []);

const load = (key) => {
  const draft = safe(() => JSON.parse(localStorage.getItem(key)));
  if (!draft || typeof draft !== "object" || typeof draft.savedAt !== "number") return null;
  return Date.now() - draft.savedAt > TTL_MS ? null : draft;
};

export const removeObservationDraft = (key) => {
  if (key) safe(() => localStorage.removeItem(key));
};

// Every draft still worth keeping, as [key, draft]. Expired or unreadable ones
// are removed on the way, whoever wrote them.
const liveDrafts = () =>
  draftKeys().reduce((live, key) => {
    const draft = load(key);
    if (draft) live.push([key, draft]);
    else removeObservationDraft(key);
    return live;
  }, []);

// What the form restores at mount: the draft for this visit or teacher when
// the URL names one; on a bare /observation, this account's newest unsent NEW
// observation, whichever teacher it was for.
export const restoreObservationDraft = ({ userUuid, visitUuid, teacherUuid }) => {
  if (!userUuid) return null;
  const live = liveDrafts();
  const key = observationDraftKey({ userUuid, visitUuid, teacherUuid });
  if (key) return live.find(([k]) => k === key)?.[1] || null;
  const prefix = `${PREFIX}${userUuid}:new:`;
  return live
    .filter(([k]) => k.startsWith(prefix))
    .reduce((newest, [, draft]) => (!newest || draft.savedAt > newest.savedAt ? draft : newest), null);
};

export const saveObservationDraft = (key, draft) => {
  if (key) safe(() => localStorage.setItem(key, JSON.stringify({ ...draft, savedAt: Date.now() })));
};

export const clearObservationDrafts = () => draftKeys().forEach(removeObservationDraft);
